import React, { useEffect, useState, useImperativeHandle, forwardRef} from "react";
import { RCCAlert, RCCGrid, RCCHookToast, RCCIcon, RCCPopover, RCCServicesApi } from "react-common-components";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import { ISwotStrategies} from "../../interface";
import { ISWOTStrategy,IStrategyObjective,IRefSwotStrategiesTab } from "./interface";
import SWOTObjective from "./Objective";

const SWOTStrategies: React.ForwardRefRenderFunction<IRefSwotStrategiesTab,ISwotStrategies> = ({
	swotId,
	canChange,
	filters
},ref) => {
	const { translate } = useTranslation();
	const { addToast} = RCCHookToast();
	
	const [ loading,setLoading] = useState<boolean>(false);
	const [ strategies,setStrategies] = useState<Array<ISWOTStrategy>>();

	const mapsArray = (objectives:Array<IStrategyObjective>) => [...new Set(objectives.map((x) => x.mapName))];

	const requestSwotStrategies = async() => {
		setLoading(true);
		try {
			const {data} = await RCCServicesApi.getRequest({
				method: "SWOTStrategies/ListStrategies",
				urlParameters:{
					swotId,
					...filters
				}	
			});

			setStrategies(data.map((x:ISWOTStrategy) => ({...x,maps:mapsArray(x.objectives).sort()} as ISWOTStrategy)));
			setLoading(false);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar estratégias SWOT"), addToast);
			setLoading(false);
		}
	};

	useImperativeHandle(ref, () => ({
		reQueryObjectives
	}));

	const reQueryObjectives = () => requestSwotStrategies();

	useEffect(() => {
		if(swotId)
			reQueryObjectives();
	}, [swotId]);

	return (
		<RCCGrid container>
			{loading ?
				<Loader/>
				:
				<>
					{strategies?.map((item) => 
						<RCCGrid container item key={v4()}>
							<RCCGrid container item
								alignItems="flex-end">
								<Title color="defaultColor" size="lg">{translate(item.taa015Titulo)}</Title>
								<Title color="grey" size="sm">
									{`${translate("Estratégia")}: ${translate(item.taa015Estrategia)} | ${translate("Ação")}: ${translate(item.taa015Acao)}`}
								</Title>
								<RCCPopover
									header={{title:translate("Descrição")}}
									body={{text:translate(item.taa015Descricao)}}
								>
									<RCCIcon
										name="infoCircle"
									/>
								</RCCPopover>
								<hr/>
								{item.objectives.length === 0 ?
									<RCCAlert
										color="blue"
										title={translate("Nenhum item encontrado")}
										description={translate("Não há itens vinculados para essa estratégia SWOT")}
									/> 
									:
									<RCCGrid container item>
										{item.maps?.map((mapName) => 
											<RCCGrid container item key={v4()}>
												{mapName &&
													<Title color="defaultColor" size="sm">{`${translate("Mapa")}: ${mapName}`}</Title>
												}
												{item.objectives.filter((y) => y.mapName === mapName)?.map((obj) => 
													<RCCGrid container item key={v4()}>
														<SWOTObjective 
															objective={obj}
															onClose={() => {reQueryObjectives();}}
															strategy={item}
															swotId={swotId}
															canChange={canChange}
														/>
													</RCCGrid>	
												)}
											</RCCGrid>	
										)}
									</RCCGrid>
								}
							</RCCGrid>
						</RCCGrid>
					)}
				</>
			}
		</RCCGrid>
	);
};

export default forwardRef(SWOTStrategies);
